/* ============================================================== */
/*                                                                */
/*   MultidisciplinaryV4  ·  sequenceของบันทึกทีมสหสาขา (HIS V4)   */
/*                                                                */
/*   practice เดียวกับ PatientOrdersV4 / ExamV4:                     */
/*     data   -> state.MultidisciplinaryV4Sequence                    */
/*     loading -> state.buttonLoadCheck["CardMultidisciplinaryV4_<ACTION>"] */
/*     error   -> state.errorMessage["CardMultidisciplinaryV4"]        */
/*     card เรียก props.runSequence({ sequence, action, card, ... })   */
/*                                                                */
/*   reference ของ HIS V3 ที่ใช้เหมือนกัน: src/HIS/Interface/PTMI.ts     */
/*     getMultidisciplinary   :509-526   (list {items:[...]})          */
/*     saveMultidisciplinary  :547-560   (POST list)                   */
/*     updateMultidisciplinary:562-574   (PUT detail/{pk})             */
/*     deleteMultidisciplinary:640-657   (DELETE detail/{pk})          */
/*     getMultidisciplinaryLogs:528-545  (list log ?item=)             */
/*   และ UI เดิม: react-lib/apps/PTM/CardMultiDisciplinaryView.tsx     */
/*     + MultiDisciplinaryEditor.tsx (Start Date + Problemบังคับ)      */
/*                                                                */
/* ============================================================== */

import ChoiceView from "issara-sdk/apis/ChoiceView_core";
import MultidisciplinaryItemDetail from "issara-sdk/apis/MultidisciplinaryItemDetail_apps_PTM";
/* ฉบับที่รับ body ตอน DELETE (apps_PTM.delete ไม่รับ data) — V3 ใช้ตัวนี้ส่ง username/password */
import MultidisciplinaryItemDetailM from "issara-sdk/apis/MultidisciplinaryItemDetail_apps_PTMM";
import MultidisciplinaryItemList from "issara-sdk/apis/MultidisciplinaryItemList_apps_PTM";
import MultidisciplinaryItemLogList from "issara-sdk/apis/MultidisciplinaryItemLogList_apps_PTM";
import MultidisciplinaryRecordLatest from "issara-sdk/apis/MultidisciplinaryRecordLatest_apps_PTM";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  PickMainState,
} from "react-lib/apps/HISV3/common/CommonInterface";

import {
  apiErrorTextOf,
  markButtonLoadCheck,
  seqBtnKey,
  seqErrorKey,
  writeSequenceState,
  type BLC_STATUS_TYPE,
  type SeqMeta,
} from "./sequenceStateV4";

import {
  ConsultDraft,
  ConsultFilter,
  ConsultIds,
  ConsultNote,
  ConsultV4Slice,
  buildConsultDeletePayload,
  buildConsultPayload,
  buildConsultUpdatePayload,
  validateConsultCredentials,
  consultListParams,
  consultLogChangesText,
  consultLogsFromApi,
  consultNoteEditable,
  consultNotesFromApi,
  consultRecordIdFromApi,
  consultRoleOptionsFromApi,
  emptyConsultV4Slice,
  normalizeConsultV4Slice,
  validateConsultDraft,
} from "../../patient/consultModelV4";
import { asNumber, asText, type Dict } from "../../patient/examModelV4";

/* ============================================================== */
/*   SECTION 1 · TYPE-DEFINING CONSTANTS                           */
/* ============================================================== */

export const SEQUENCE_NAME = "MultidisciplinaryV4" as const;

/** card ผู้ส่ง action (ตัวเดียวกันกับหน้า Nurse Note แต่คนละ key กันเพื่อไม่ให้ error ซ้อนกัน) */
export const CARD_NAME = "CardMultidisciplinaryV4";

export const ACTIONS = {
  LOAD: "LOAD",
  LOAD_LOGS: "LOAD_LOGS",
  CREATE: "CREATE",
  UPDATE: "UPDATE",
  DELETE: "DELETE",
} as const;

/** key ใน state.buttonLoadCheck เช่น CardMultidisciplinaryV4_CREATE */
export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

/** key ของ state ที่ sequence นี้เขียน (export ให้ MainHIS/test/UI อ้างชื่อเดียวได้) */
export const CONSULT_SLICE_KEY = "MultidisciplinaryV4Sequence";

/* ============================================================== */
/*   SECTION 2 · STATE                                             */
/* ============================================================== */

export type State = Partial<{
  /** Partial<...> เพราะ RunSequence เขียน { sequenceIndex: "START" } ก่อนเรียก START */
  MultidisciplinaryV4Sequence: Partial<ConsultV4Slice> | null;
}>;

export const StateInitial: State = { MultidisciplinaryV4Sequence: null };

export type Event = { message: "RunSequence"; params: Record<string, unknown> };

export type Data = { division?: number; device?: number };

export const DataInitial: Data = {};

export type Props = {};

export type ConsultSequenceState = ConsultV4Slice;

/* ============================================================== */
/*   SECTION 3 · PARAMS TYPE                                       */
/* ============================================================== */

type SequenceName = typeof SEQUENCE_NAME;
type Actions = typeof ACTIONS;
export type ActionName = Actions[keyof Actions];

export type ConsultIdsInput = Partial<ConsultIds> & { ids?: ConsultIds };

export type SeqState = BaseSeqState<SequenceName> &
  ConsultIdsInput & {
    card?: string;
    filter?: Partial<ConsultFilter>;
    encounter?: { id?: unknown };
  };

export type ActionPayloadMap = {
  [ACTIONS.LOAD]: ConsultIdsInput & { filter?: Partial<ConsultFilter> };
  [ACTIONS.LOAD_LOGS]: ConsultIdsInput & { itemId: number };
  [ACTIONS.CREATE]: ConsultIdsInput & { draft: ConsultDraft };
  [ACTIONS.UPDATE]: ConsultIdsInput & { draft: ConsultDraft; noteId: number };
  [ACTIONS.DELETE]: ConsultIdsInput & { noteId: number; username?: string; password?: string };
};

type PickedState = PickMainState<"buttonLoadCheck" | "errorMessage">;

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State,
  SeqState
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];
export type ConsultRunSequence = RunSequence;

export type ConsultRunParams = {
  [K in ActionName]: {
    sequence: typeof SEQUENCE_NAME;
    action: K;
    card?: string;
    restart?: boolean;
    clear?: boolean;
  } & ActionPayloadMap[K];
}[ActionName];

/** params แบบกว้างที่ card ประกอบ (payload บาง action TS ตรวจไม่ครบเพราะ spread) */
export type ConsultRunParamsLoose = {
  sequence: string;
  action: string;
  card?: string;
  restart?: boolean;
  clear?: boolean;
  [key: string]: unknown;
};

export type ConsultRunSequenceProp = (params: ConsultRunParams | ConsultRunParamsLoose) => void;

export type SetProp = ActionTypes["SetProp"];
export type OnEvent = ActionTypes["OnEvent"];

/** payload + ids/card ที่ inject ให้ทุก handler */
type Ctx<A extends ActionName> = ConsultIdsInput & {
  ids: ConsultIds;
  card?: string;
  action?: A;
  filter?: Partial<ConsultFilter>;
  draft?: ConsultDraft;
  noteId?: number;
  itemId?: number;
  /** credentials สำหรับกรณีลบ (V3 ใช้ modalยืนยันก่อนลบ) */
  username?: string;
  password?: string;
};

type CombinedState = PickedState & State;

export type Handler<P = unknown, R = void> = ActionHandler<CombinedState, Event, Data, P, R>;

type Ctl = {
  getState: () => CombinedState & Record<string, any>;
  setState: (state: any, callback?: () => void) => any;
  handleEvent: (event: Event) => void;
  apiToken?: string;
  data?: Data;
};

/* ============================================================== */
/*   SECTION 4 · INTERNAL HELPERS                                  */
/* ============================================================== */

type ApiTuple = [unknown, unknown, unknown];

const extraOf = (controller: Ctl) => ({
  division: controller.data?.division,
  device: controller.data?.device,
});

const errorMessageOf = (result: ApiTuple): string | null =>
  /* tuple ของ BaseService คือ [responseData, errorBody, axiosResult]
     apiErrorTextOf ตรวจ isAxiosError ไม่ใช่ truthiness ของช่องที่ 3 */
  apiErrorTextOf(result, { fallbackText: "เชื่อมต่อเซิร์ฟเวอร์ไม่ได้" });

const dataOf = (result: ApiTuple): unknown => result[0];

const sliceOf = (controller: Ctl): ConsultV4Slice =>
  normalizeConsultV4Slice(controller.getState().MultidisciplinaryV4Sequence);

type SliceMeta = { card?: string; action?: string };

const metaOf = (meta: SliceMeta): SeqMeta => ({
  sequence: SEQUENCE_NAME,
  card: meta.card ?? CARD_NAME,
  action: meta.action,
});

export const consultBtnKey = (meta: SliceMeta): string => seqBtnKey(metaOf(meta));
export const consultErrorKey = (meta: SliceMeta): string => seqErrorKey(metaOf(meta));

type WritePatch = Partial<ConsultV4Slice> & {
  loading?: boolean;
  saving?: boolean;
  error?: string | null;
};

const writeSlice = (controller: Ctl, meta: SliceMeta, patch: WritePatch): any =>
  writeSequenceState<ConsultV4Slice>(
    controller,
    metaOf(meta),
    CONSULT_SLICE_KEY,
    sliceOf(controller),
    { ...patch, sequenceIndex: "Action" },
    emptyConsultV4Slice,
  );

const setBtn = (controller: Ctl, metas: SliceMeta[], status: BLC_STATUS_TYPE): void =>
  markButtonLoadCheck(controller, metas.map(metaOf), status);

const reportError = (controller: Ctl, meta: SliceMeta, message: string): void => {
  writeSlice(controller, { ...meta, action: meta.action }, { error: message });
};

const numberOrUndefined = (value: unknown): number | undefined => asNumber(value);

/** user ปัจจุบัน (backend ใช้ practitioner ผูกบันทึกกับคนเขียน + ล็อกแก้เฉพาะของตนเอง) */
const currentUserIdOf = (controller: Ctl): number | undefined => {
  const app = controller.getState() as {
    django?: { user?: { id?: unknown } };
    currentDoctor?: { id?: unknown };
    selectedEmr?: { doctor?: unknown };
  };
  return (
    numberOrUndefined(app.django?.user?.id) ??
    numberOrUndefined(app.currentDoctor?.id) ??
    numberOrUndefined(app.selectedEmr?.doctor)
  );
};

const currentTokenOf = (controller: Ctl): string | undefined => {
  const app = controller.getState() as { django?: { user?: { token?: unknown } } };
  const token = app.django?.user?.token;
  return typeof token === "string" && token !== "" ? token : undefined;
};

/** encounter id จาก params / slice / state shared ของ MainHIS */
const readIds = (controller: Ctl, params: ConsultIdsInput): ConsultIds => {
  const slice = sliceOf(controller);
  const app = controller.getState() as { selectedEncounter?: { id?: unknown }; encounter?: { id?: unknown } };
  const fromParams: Partial<ConsultIds> = params.ids ?? params;

  return {
    encounterId:
      fromParams.encounterId ??
      slice.ids.encounterId ??
      numberOrUndefined(app.selectedEncounter?.id) ??
      numberOrUndefined((params as { encounterId?: unknown }).encounterId),
    emrId: fromParams.emrId ?? slice.ids.emrId,
    patientId: fromParams.patientId ?? slice.ids.patientId,
  };
};

const filterOf = (controller: Ctl, params: ConsultIdsInput & { filter?: Partial<ConsultFilter> }): ConsultFilter => {
  const slice = sliceOf(controller);
  const next = params.filter;
  if (next === undefined) return slice.filter;
  /* UI ส่ง filter ครบชุดทุกครั้ง (กันค่าค้างจากรอบก่อน) แต่ถ้าส่ง field เดียวก็ merge ได้ */
  return { ...slice.filter, ...next };
};

/* ============================================================== */
/*   SECTION 5 · READ                                              */
/* ============================================================== */

/**
 * role options ของ dropdown "วิชาชีพ"
 * V3 ใช้ getChoices(model="users.Role", field="type", nameAsId=true) — CardMultiDisciplinaryView.tsx:180-195
 * ถ้า choices endpoint ล้ม ให้ fallback เป็น [] (UI จะกรองด้วย label ที่พบในแถวจริงแทน)
 */
const loadRoleOptions = async (controller: Ctl): Promise<{ options: ConsultV4Slice["roleOptions"]; error: string | null }> => {
  const result = await ChoiceView.get({
    params: { model: "users.Role", field: "type", name_as_id: true },
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });

  const error = errorMessageOf(result);
  return { options: error ? [] : consultRoleOptionsFromApi(dataOf(result)), error: null };
};

const Load: Handler<Ctx<"LOAD">> = async (controller, params) => {
  const ids = readIds(controller, params);
  const filter = filterOf(controller, params);

  if (!ids.encounterId) {
    return writeSlice(controller, params, { ids, filter, notes: [], total: 0, error: "ยังไม่ได้เลือกผู้ป่วย (encounter)" });
  }

  const { options } = await loadRoleOptions(controller);

  const result = await MultidisciplinaryItemList.list({
    params: consultListParams({ encounterId: ids.encounterId, filter }),
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });

  const error = errorMessageOf(result);
  const notes = error ? [] : consultNotesFromApi(dataOf(result), options);
  const data = asDictish(dataOf(result));

  writeSlice(controller, params, {
    ids,
    filter,
    roleOptions: options,
    notes,
    total: typeof data.total === "number" ? data.total : notes.length,
    error,
  });
};

const asDictish = (payload: unknown): Dict => (payload !== null && typeof payload === "object" ? (payload as Dict) : {});

const LoadLogs: Handler<Ctx<"LOAD_LOGS">> = async (controller, params) => {
  const ids = readIds(controller, params);
  const itemId = params.itemId;

  if (itemId === undefined) {
    return writeSlice(controller, params, { ids, error: "ยังไม่ได้เลือกรายการบันทึก" });
  }

  const result = await MultidisciplinaryItemLogList.list({
    params: { item: itemId },
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });

  const error = errorMessageOf(result);
  const logs = error ? [] : consultLogsFromApi(dataOf(result));
  const previous = sliceOf(controller).logsByItem;

  writeSlice(controller, params, {
    ids,
    logs,
    logsByItem: { ...previous, [`${itemId}`]: logs },
    error,
  });
};

/* ============================================================== */
/*   SECTION 6 · WRITE                                             */
/* ============================================================== */

/**
 * POST /apis/PTM/multidisciplinary-item/
 * V3 editor บังคับ Start Date + Problem (MultiDisciplinaryEditor.tsx:93-111)
 * และแก้/ลบได้เฉพาะบันทึกของตัวเอง (practitioner === user ปัจจุบัน) — CardMultiDisciplinaryView.tsx:223-235
 */
const Create: Handler<Ctx<"CREATE">> = async (controller, params) => {
  const ids = readIds(controller, params);
  const draft = params.draft;

  if (!ids.encounterId) return writeSlice(controller, params, { ids, error: "ยังไม่ได้เลือกผู้ป่วย" });
  if (draft === undefined) return writeSlice(controller, params, { ids, error: "ไม่มีข้อมูลบันทึกที่จะบันทึก" });

  const valid = validateConsultDraft(draft);
  if (!valid.ok) return writeSlice(controller, params, { ids, error: valid.error });

  const slice = sliceOf(controller);
  /* record หัวของ encounter (ถ้า backend ผูก item กับ record) — retrieve แบบเดียวกับ V3 latest */
  const recordResult = await MultidisciplinaryRecordLatest.retrieve({
    params: { encounter: ids.encounterId },
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });
  const recordId = errorMessageOf(recordResult) === null ? consultRecordIdFromApi(dataOf(recordResult)) : null;

  writeSlice(controller, params, { ids, saving: true, error: null });

  const result = await MultidisciplinaryItemList.create({
    data: buildConsultPayload({
      encounterId: ids.encounterId,
      draft,
      practitionerId: currentUserIdOf(controller) ?? null,
      recordId,
      /* backend ยืนยันตัวผู้บันทึกด้วย username/password (V3 editor ส่งสองfield เสมอ) */
      username: draft.username,
      password: draft.password,
    }) as never,
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });

  const error = errorMessageOf(result);
  if (error) return writeSlice(controller, params, { ids, saving: false, error });

  await Load(controller, { action: ACTIONS.LOAD, card: params.card, ids, filter: slice.filter });
  writeSlice(controller, params, { ids, saving: false, savedMessage: "เพิ่มข้อมูล Multidisciplinary แล้ว", error: null });
};

const Update: Handler<Ctx<"UPDATE">> = async (controller, params) => {
  const ids = readIds(controller, params);
  const { noteId, draft } = params;

  if (noteId === undefined || draft === undefined) {
    return writeSlice(controller, params, { ids, error: "ไม่มีรายการบันทึกที่ต้องการแก้ไข" });
  }

  const slice = sliceOf(controller);
  const note = slice.notes.find((item: ConsultNote) => item.id === noteId);

  if (note === undefined) {
    return writeSlice(controller, params, { ids, error: "ไม่พบบันทึกรายการนี้ใน Encounter นี้" });
  }

  const valid = validateConsultDraft(draft);
  if (!valid.ok) return writeSlice(controller, params, { ids, error: valid.error });

  const userId = currentUserIdOf(controller);
  if (!consultNoteEditable(note, userId)) {
    return writeSlice(controller, params, {
      ids,
      error: "แก้ไขได้เฉพาะบันทึกของผู้ใช้ที่สร้างเอง (ผู้บันทึกคนละคน)",
    });
  }

  writeSlice(controller, params, { ids, saving: true, error: null });

  const result = await MultidisciplinaryItemDetail.update({
    pk: noteId,
    data: buildConsultUpdatePayload({
      encounterId: ids.encounterId,
      note,
      draft,
      username: draft.username,
      password: draft.password,
    }) as never,
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });

  const error = errorMessageOf(result);
  if (error) return writeSlice(controller, params, { ids, saving: false, error });

  await Load(controller, { action: ACTIONS.LOAD, card: params.card, ids, filter: slice.filter });
  writeSlice(controller, params, { ids, saving: false, savedMessage: "บันทึกการแก้ไขแล้ว", error: null });
};

/**
 * DELETE /apis/PTM/multidisciplinary-item/{pk}/ พร้อม body {username, password}
 * V3 ทำแบบเดียวกัน: modal "ระบุ Username และ Password เพื่อลบ" (CardMultiDisciplinaryView.tsx:625-632)
 * แล้วส่งเข้า MultidisciplinaryItemDetail_apps_PTMM.delete (PTMI.ts:640-657 — comment ในโค้ดเขียนว่า
 * "shouldn't be like this but copied from old code")
 * apps_PTM.delete ไม่มี `data` ใน generated signature จึงต้องใช้ฉบับ PTMM
 */
const Delete: Handler<Ctx<"DELETE">> = async (controller, params) => {
  const ids = readIds(controller, params);
  const noteId = params.noteId;

  if (noteId === undefined) return writeSlice(controller, params, { ids, error: "ยังไม่ได้เลือกรายการบันทึก" });

  const slice = sliceOf(controller);
  const note = slice.notes.find((item: ConsultNote) => item.id === noteId);
  const userId = currentUserIdOf(controller);

  if (note !== undefined && !consultNoteEditable(note, userId)) {
    return writeSlice(controller, params, { ids, error: "ลบได้เฉพาะบันทึกของผู้ใช้ที่สร้างเอง" });
  }

  const credentials = { username: params.username ?? "", password: params.password ?? "" };
  const valid = validateConsultCredentials(credentials);
  if (!valid.ok) return writeSlice(controller, params, { ids, error: valid.error });

  writeSlice(controller, params, { ids, saving: true, error: null });

  const result = await MultidisciplinaryItemDetailM.delete({
    pk: noteId,
    data: buildConsultDeletePayload(credentials),
    apiToken: controller.apiToken,
    extra: extraOf(controller),
  });

  const error = errorMessageOf(result);
  if (error) return writeSlice(controller, params, { ids, saving: false, error });

  await Load(controller, { action: ACTIONS.LOAD, card: params.card, ids, filter: slice.filter });
  writeSlice(controller, params, { ids, saving: false, savedMessage: "ลบบันทึกทีมสหสาขาแล้ว", error: null });
};

/* ============================================================== */
/*   SECTION 7 · SEQUENCE ENTRY / DISPATCH                         */
/* ============================================================== */

const LOAD_METAS: SliceMeta[] = [{ card: CARD_NAME, action: ACTIONS.LOAD }];

export const Start: Handler<SeqState> = async (controller, params) => {
  const ids = readIds(controller, params);
  const filter = filterOf(controller, params);

  controller.setState({
    MultidisciplinaryV4Sequence: { ...emptyConsultV4Slice(), ids, filter, sequenceIndex: "Action" },
  });
  setBtn(controller, LOAD_METAS, BLC_STATUS.LOADING);

  await Load(controller, { action: ACTIONS.LOAD, card: CARD_NAME, ids, filter });
};

export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionName]: Handler<Ctx<K>> }> = {
    [ACTIONS.LOAD]: Load,
    [ACTIONS.LOAD_LOGS]: LoadLogs,
    [ACTIONS.CREATE]: Create,
    [ACTIONS.UPDATE]: Update,
    [ACTIONS.DELETE]: Delete,
  };

  const action = params.action as ActionName;
  const meta: SliceMeta = { card: (params as SliceMeta).card, action };
  const handler = actionHandlers[action] as Handler<Ctx<ActionName>> | undefined;

  if (handler) {
    return handler(controller, {
      ...params,
      action,
      ids: readIds(controller, params as ConsultIdsInput),
    } as Ctx<ActionName>);
  }

  console.error(`No handler found for action: ${String(action)}`);
  return reportError(controller, meta, `ไม่รู้จัก action "${asText(action)}" ใน ${SEQUENCE_NAME}`);
};

/** ให้ UI/test ตรวจว่า log text map ถูกใช้ต่อ (กัน helperตายเงียบ) */
export { consultLogChangesText };
